/* eslint-disable max-len */
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import Button from 'components/Basic/Button';
import { toggleHideSatelliteOverlay } from 'store/slices/ToolSlice';
import { appToast } from 'components/Basic/CustomToast';
import InvalidFacetEditToast from '../../Group/InvalidModal';

const SHORTCUT_KEY = 'h';
const MAP_ONLY_TOAST_ID = 'map-only-disabled-banner';

const NON_TEXT_INPUT_TYPES = ['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image'];

export function isTypingInField(target: EventTarget | null) {
	const el = target as HTMLElement | null;
	if (!el) return false;
	if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
	if (el.tagName === 'INPUT') return !NON_TEXT_INPUT_TYPES.includes((el as HTMLInputElement).type);
	return false;
}

export default function ToggleSatelliteViewBtn() {
	const { hideSatelliteOverlay, threeDModelEnabled, fullScreenEnabled } = useSelector((state: RootState) => state.toolNewPostions.data);
	const dispatch = useDispatch();

	useEffect(() => {
		function handleKeyUp(event: KeyboardEvent) {
			if (event.ctrlKey || event.metaKey || event.altKey) return;
			if (isTypingInField(event.target)) return;

			if (event.key === 'Escape') {
				if (hideSatelliteOverlay) dispatch(toggleHideSatelliteOverlay());
				return;
			}

			if (event.key.toLowerCase() !== SHORTCUT_KEY) return;

			dispatch(toggleHideSatelliteOverlay());
		}

		window.addEventListener('keyup', handleKeyUp);
		return () => window.removeEventListener('keyup', handleKeyUp);
	}, [dispatch, hideSatelliteOverlay]);

	useEffect(() => {
		if (hideSatelliteOverlay) {
			appToast.sticky(
				<InvalidFacetEditToast
					label="All other functionality is disabled in map only view. Exit map only to enable them."
					message=""
					labelStyle="message"
					showUndo={false}
					showExit
					onExit={() => dispatch(toggleHideSatelliteOverlay())}
				/>,
				{ toastId: MAP_ONLY_TOAST_ID, className: 'invalid-error-toast', containerId: 'tool-toast', closeButton: false }
			);
		} else if (appToast.isActive(MAP_ONLY_TOAST_ID)) {
			appToast.dismiss({ id: MAP_ONLY_TOAST_ID, containerId: 'tool-toast' });
		}
	}, [hideSatelliteOverlay]);

	if (threeDModelEnabled) return null;

	return (
		<Button
			id="toggle-satellite-view-btn"
			className={`toggleSatelliteViewBtn ${
				hideSatelliteOverlay ? 'active' : ''
			} ${fullScreenEnabled ? ' fullscreenview-satelite' : 'defaultscreenview-satelite'}`}
			onClick={() => dispatch(toggleHideSatelliteOverlay())}
			tooltipContent={`${hideSatelliteOverlay ? 'Exit map only' : 'Show map only'} (h)`}
		>
			{hideSatelliteOverlay ? (
				<svg
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M2 12C3.8 8.6 7.3 6 12 6C16.7 6 20.2 8.6 22 12C20.2 15.4 16.7 18 12 18C7.3 18 3.8 15.4 2 12Z"
						stroke="var(--primary)"
						strokeWidth="1.7"
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					<circle
						cx="12"
						cy="12"
						r="2.6"
						stroke="var(--primary)"
						strokeWidth="1.7"
					/>
					<path
						d="M5 19L19 5"
						stroke="var(--primary)"
						strokeWidth="1.7"
						strokeLinecap="round"
					/>
				</svg>
			) : (
				<svg
					width="20"
					height="20"
					viewBox="0 0 24 24"
					fill="none"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path
						d="M2.5 12C4.3 8.6 7.7 6 12 6C16.3 6 19.7 8.6 21.5 12C19.7 15.4 16.3 18 12 18C7.7 18 4.3 15.4 2.5 12Z"
						stroke="var(--defaultIconColor)"
						strokeWidth="2"
						strokeLinejoin="round"
					/>
					<circle
						cx="12"
						cy="12"
						r="3"
						stroke="var(--defaultIconColor)"
						strokeWidth="2"
					/>
				</svg>
			)}
		</Button>
	);
}
